import React, { useMemo, useState } from "react";
import {
    ArrowLeft,
    Building2,
    Mail,
    Phone,
    ShieldCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import siteLogo from "../assets/equitylogo.png";

import apiendpoints from "../apiendpoints.json";

const CURRENCIES = ["INR"];

export default function SecurePayment() {
    const navigate = useNavigate();

    const { executeRecaptcha } = useGoogleReCaptcha();

    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        phone: "",
        currency: "INR",
        amount: "",
        companyName: "",
        agreement: false,
    });

    const [errors, setErrors] = useState({});

    const validateField = (name, value) => {
        if (name === "fullName") {
            return value.trim()
                ? ""
                : "Full Name is required.";
        }

        if (name === "email") {
            if (!value.trim()) {
                return "Email Address is required.";
            }

            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
                ? ""
                : "Please enter a valid email address.";
        }

        if (name === "currency") {
            return CURRENCIES.includes(value)
                ? ""
                : "Currency is required.";
        }

        if (name === "amount") {
            if (
                value === "" ||
                value === null ||
                value === undefined
            ) {
                return "Base Price is required.";
            }

            return Number(value) > 0
                ? ""
                : "Base Price must be greater than 0.";
        }

        if (name === "agreement") {
            return value
                ? ""
                : "You must accept the Terms & Conditions and Refund Policy.";
        }

        return "";
    };

    const validateForm = (data = formData) => {
        const nextErrors = {};

        const fullNameError = validateField(
            "fullName",
            data.fullName
        );

        if (fullNameError) {
            nextErrors.fullName = fullNameError;
        }

        const emailError = validateField(
            "email",
            data.email
        );

        if (emailError) {
            nextErrors.email = emailError;
        }

        const currencyError = validateField(
            "currency",
            data.currency
        );

        if (currencyError) {
            nextErrors.currency = currencyError;
        }

        const amountError = validateField(
            "amount",
            data.amount
        );

        if (amountError) {
            nextErrors.amount = amountError;
        }

        const agreementError = validateField(
            "agreement",
            data.agreement
        );

        if (agreementError) {
            nextErrors.agreement = agreementError;
        }

        return nextErrors;
    };

    const handleChange = (e) => {
        const {
            name,
            value,
            checked,
            type,
        } = e.target;

        const nextValue =
            type === "checkbox"
                ? checked
                : value;

        setFormData((prev) => ({
            ...prev,
            [name]: nextValue,
        }));

        const fieldError = validateField(
            name,
            nextValue
        );

        setErrors((prev) => ({
            ...prev,
            [name]: fieldError,
        }));
    };

    const ledger = useMemo(() => {
        const amount = Number(
            formData.amount || 0
        );

        const subtotal = amount;

        const platformFee =
            subtotal * 0.0236;

        const total =
            subtotal + platformFee;

        return {
            amount,
            subtotal,
            platformFee,
            total,
        };
    }, [
        formData.amount,
        formData.currency,
    ]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const nextErrors = validateForm();

        setErrors(nextErrors);

        if (
            Object.keys(nextErrors).length > 0
        ) {
            return;
        }

        if (!executeRecaptcha) {
            alert(
                "reCAPTCHA is not ready. Please try again."
            );

            return;
        }

        try {
            // Generate a fresh reCAPTCHA v3 token
            const captchaToken =
                await executeRecaptcha(
                    "payment"
                );
    // console.log("reCAPTCHA v3 Token:", captchaToken);
            if (!captchaToken) {
                alert(
                    "reCAPTCHA verification failed. Please try again."
                );

                return;
            }

            const payload = {
                fullName:
                    formData.fullName,

                email:
                    formData.email,

                phone:
                    formData.phone,

                companyName:
                    formData.companyName,

                currency:
                    formData.currency,

                baseAmount:
                     ledger.total,

                platformFee:
                    ledger.platformFee,

          

                captchaToken:
                    captchaToken,
            };

            const response = await fetch(
                `${apiendpoints.equityUrl}/api/CreatePayment`,
                {
                    method: "POST",

                    headers: {
                        "Content-Type":
                            "application/json",
                    },

                    body: JSON.stringify(
                        payload
                    ),
                }
            );

            const payuData =
                await response.json();

            if (!response.ok) {
                alert(
                    payuData.message ||
                    "Payment initiation failed"
                );

                return;
            }

            redirectToPayU(payuData);

        } catch (err) {
            console.error(err);

            alert(
                "Something went wrong."
            );
        }
    };

    const redirectToPayU = (payuData) => {
        const form =
            document.createElement(
                "form"
            );

        form.method = "POST";

        form.action =
            payuData.paymentUrl;

        form.style.display =
            "none";

        Object.entries(payuData).forEach(
            ([key, value]) => {
                const input =
                    document.createElement(
                        "input"
                    );

                input.type =
                    "hidden";

                input.name =
                    key;

                input.value =
                    value;

                form.appendChild(
                    input
                );
            }
        );

        document.body.appendChild(
            form
        );

        form.submit();
    };

    return (
        <div className="secure-payment-page min-h-screen overflow-x-hidden overflow-y-auto bg-gradient-to-b from-[#f7fbff] via-white to-[#f2fbf5]">

            {/* Header */}

            <header className="sticky top-0 z-50 border-b border-[#dbe7f4] bg-white/95 shadow-sm backdrop-blur">

                <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-2 sm:flex-row sm:items-center sm:justify-between sm:px-6">

                    <div className="flex items-center gap-3">

                        <img
                            src={siteLogo}
                            alt="Logo"
                            className="h-9 w-auto object-contain sm:h-10"
                        />

                    </div>

                    <div className="flex items-center justify-between gap-4 sm:gap-6">

                        <div className="hidden lg:flex items-center gap-5">

                            <div className="flex items-center gap-2 text-sm">

                                <ShieldCheck
                                    className="text-green-600"
                                    size={18}
                                />

                                SSL Encrypted

                            </div>

                            <div className="flex items-center gap-2 text-sm">

                                <ShieldCheck
                                    className="text-green-600"
                                    size={18}
                                />

                                PCI DSS

                            </div>

                        </div>

                        <button
                            type="button"
                            onClick={() => navigate("/")}
                            className="flex items-center gap-2 text-sm text-blue-600 sm:text-base"
                        >
                            <ArrowLeft size={18} />
                            Back
                        </button>

                    </div>

                </div>

            </header>

            {/* Main */}

            <div className="mx-auto max-w-[1280px] px-3 py-3 sm:px-4 sm:py-4">

                <form
                    onSubmit={handleSubmit}
                    className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-stretch"
                >

                    {/* LEFT SIDE */}

                    <div>

                        <h1 className="text-2xl font-bold leading-tight sm:text-[24px]">

                            Secure Payment

                        </h1>

                        <p className="mt-1 text-sm text-gray-500">

                            Fast, secure and easy payment experience.

                        </p>

                        {/* Contact Details */}

                        <div className="mt-4 rounded-2xl border border-[#dbe7f4] bg-white p-4 shadow-sm sm:p-5">

                            <h2 className="text-xl font-semibold">

                                Contact Details

                            </h2>

                            <p className="mt-1 text-sm text-gray-500">

                                Please provide your billing information.

                            </p>

                            <div className="mt-4 grid gap-4 md:grid-cols-2">

                                {/* Full Name */}

                                <div>

                                    <label className="mb-2 block text-sm font-medium">

                                        Full Name <span className="text-red-500">*</span>

                                    </label>

                                    <div className="relative flex items-center">

                                        <Building2
                                            className="absolute left-3  text-gray-400"
                                            size={18}
                                        />

                                        <input
                                            type="text"
                                            name="fullName"
                                            value={formData.fullName}
                                            onChange={handleChange}
                                            placeholder="Full Name"
                                            className={`w-full rounded-xl border pl-10 pr-4 py-3 text-sm outline-none 
    focus:border-green-500 focus:ring-1 focus:ring-green-500 sm:text-base ${errors.fullName
                                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                                    : ""
                                                }`}
                                        />

                                    </div>

                                    {errors.fullName && (
                                        <p className="mt-1 text-sm text-red-500">
                                            {errors.fullName}
                                        </p>
                                    )}

                                </div>

                                {/* Email */}

                                <div>

                                    <label className="mb-2 block text-sm font-medium">

                                        Email Address <span className="text-red-500">*</span>

                                    </label>

                                    <div className="relative flex items-center">

                                        <Mail
                                            className="absolute left-3  text-gray-400"
                                            size={18}
                                        />

                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="abc@gmail.com"
                                            className={`w-full rounded-xl border pl-10 pr-4 py-3 text-sm outline-none 
    focus:border-green-500 focus:ring-1 focus:ring-green-500 sm:text-base ${errors.fullName
                                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                                    : ""
                                                }`}
                                        />

                                    </div>

                                    {errors.email && (
                                        <p className="mt-1 text-sm text-red-500">
                                            {errors.email}
                                        </p>
                                    )}

                                </div>

                            </div>

                            {/* Phone */}

                            <div className="mt-4">

                                <label className="mb-2 block text-sm font-medium">

                                    Mobile Number

                                </label>

                                <div className="relative  flex items-center">

                                    <Phone
                                        className="absolute left-3  text-gray-400"
                                        size={18}
                                    />

                                    <input
                                        type="text"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="+91 XXXXXXXXXX"
                                        className={`w-full rounded-xl border pl-10 pr-4 py-3 text-sm outline-none 
    focus:border-green-500 focus:ring-1 focus:ring-green-500 sm:text-base ${errors.fullName
                                                ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                                : ""
                                            }`} />

                                </div>

                            </div>

                        </div>

                        {/* Payment Details */}

                        <div className="mt-4 rounded-3xl border border-[#dbe7f4] bg-white p-4 shadow sm:p-5">

                            <h2 className="text-xl font-semibold">
                                Payment Details
                            </h2>

                            <p className="mt-1 text-sm text-gray-500">
                                Enter payment information.
                            </p>

                            <div className="mt-4 grid gap-4 md:grid-cols-2">

                                {/* Currency */}

                                <div>

                                    <label className="mb-2 block text-sm font-medium">
                                        Currency <span className="text-red-500">*</span>
                                    </label>

                                    <select
                                        name="currency"
                                        value={formData.currency}
                                        onChange={handleChange}
                                        className={`w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-[#1d4ed8] sm:text-base ${errors.currency
                                            ? "border-red-500 focus:border-red-500"
                                            : ""
                                            }`}
                                    >

                                        {CURRENCIES.map((item) => (

                                            <option
                                                key={item}
                                                value={item}
                                            >
                                                {item}
                                            </option>

                                        ))}

                                    </select>

                                    {errors.currency && (
                                        <p className="mt-1 text-sm text-red-500">
                                            {errors.currency}
                                        </p>
                                    )}

                                </div>
                                {/* AMOUNT */}
                                <div>
                                    <label className="mb-2 block text-sm font-medium">
                                        Service Amount{" "}
                                        <span className="text-red-500">*</span>
                                    </label>

                                    <div className="relative flex items-center">
                                        {/* Currency Symbol */}
                                        <span className="absolute left-3 text-gray-500 font-medium">₹</span>

                                        <input
                                            type="digit"
                                            name="amount"
                                            value={formData.amount}
                                            onChange={handleChange}
                                            placeholder="0.00"
                                            step="0.01"
                                            min="1"
                                            className={`w-full rounded-xl border py-3 pl-8 pr-4 outline-none transition-all 
        ${errors.amount ? "border-red-500" : "border-gray-300"} 
        focus:border-green-600 focus:ring-1 focus:ring-green-600`}
                                        />
                                    </div>

                                    {errors.amount && (
                                        <p className="mt-1 text-sm text-red-500">
                                            {errors.amount}
                                        </p>
                                    )}
                                </div>

                            </div>

                            {/* Company */}

                            {/* <div className="mt-4 grid gap-4 md:grid-cols-2">

                                <div className="md:col-span-2">

                                    <label className="mb-2 block text-sm font-medium">
                                        Company Name
                                    </label>

                                    <input
                                        type="text"
                                        name="companyName"
                                        value={formData.companyName}
                                        onChange={handleChange}
                                        placeholder="Company Name"
                                        className="w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-[#1d4ed8] sm:text-base"
                                    />

                                </div> */}

                            {/* </div> */}

                        </div>

                        {/* Agreement */}

                        <div className="mt-4 rounded-3xl border border-[#dbe7f4] bg-white p-4 shadow sm:p-5">

                            <label className="flex items-start gap-3">

                                <input
                                    type="checkbox"
                                    name="agreement"
                                    checked={formData.agreement}
                                    onChange={handleChange}
                                    className={`mt-1 ${errors.agreement
                                        ? "ring-2 ring-red-500 ring-offset-2"
                                        : ""
                                        }`}
                                />
                                <span className="text-sm text-gray-600">
                                    I agree to the
                                    <a href="/equity-policy/termsandcondition" className="text-blue-600 underline hover:text-blue-800"> Terms & Conditions</a>
                                    {" and "}
                                    <a href="/equity-policy/refund-policy" className="text-blue-600 underline hover:text-blue-800">Refund Policy</a>.
                                </span>

                            </label>

                            {errors.agreement && (
                                <p className="mt-2 text-sm text-red-500">
                                    {errors.agreement}
                                </p>
                            )}

                        </div>

                    </div>

                    {/* RIGHT SIDE */}

                    <div className="flex justify-center lg:items-center lg:justify-end">

                        <div className="w-full max-w-none rounded-2xl bg-gradient-to-br from-[#0f172a] via-[#111827] to-[#0b3b2e] p-4 text-white shadow-lg lg:max-w-[340px]">

                            <h2 className="text-base font-semibold sm:text-lg">
                                Payment Summary
                            </h2>

                            <div className="mt-4 text-center">

                                <p className="text-sm text-gray-300">
                                    Total Payable
                                </p>

                                <h1 className="mt-2 text-2xl font-bold sm:text-3xl">

                                    {formData.currency} {ledger.total.toFixed(2)}

                                </h1>

                            </div>

                            <div className="mt-5 space-y-3 text-sm sm:mt-6 sm:text-base">

                                <div className="flex justify-between">

                                    <span>Base Price</span>

                                    <span>

                                        {ledger.amount.toFixed(2)}

                                    </span>

                                </div>

                                <div className="flex justify-between">

                                    <span>Subtotal</span>

                                    <span>

                                        {ledger.subtotal.toFixed(2)}

                                    </span>

                                </div>

                                <div className="flex justify-between">

                                    <span>Platform Fee</span>

                                    <span>

                                        {ledger.platformFee.toFixed(2)}

                                    </span>

                                </div>

                                <hr className="border-gray-700" />

                                <div className="flex justify-between text-lg font-bold">

                                    <span>Total</span>

                                    <span>

                                        {ledger.total.toFixed(2)}

                                    </span>

                                </div>

                            </div>

                            {/* <div className="mt-5 rounded-xl border border-white/10 bg-white/5 p-2"> */}

                            {/* <label className="flex items-center gap-3"> */}

                            {/* <div className="w-full overflow-visible rounded-md bg-transparent">
                                        <ReCAPTCHA
                                            sitekey={RECAPTCHA_SITE_KEY}
                                            onChange={(token) => {
                                                setCaptchaToken(token || "");
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    captchaToken: token
                                                        ? ""
                                                        : "Please verify you are not a robot.",
                                                }));
                                            }}
                                            onExpired={() => {
                                                setCaptchaToken("");
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    captchaToken: "Please verify you are not a robot.",
                                                }));
                                            }}
                                        />
                                    </div> */}

                            {/* </label> */}

                            {/* {errors.captchaToken && (
                                    <p className="mt-2 text-xs text-red-400">
                                        {errors.captchaToken}
                                    </p>
                                )}

                            </div> */}

                            <button
                                type="submit"
                                className="mt-5 w-full rounded-xl bg-gradient-to-r from-[#1d4ed8] to-[#16a34a] py-3 text-sm font-semibold transition hover:opacity-95 sm:mt-6 sm:text-base"
                            >

                                Pay Now

                            </button>

                            <p className="mt-3 text-center text-xs text-gray-400">

                                Payments are securely processed by PayU.
                            </p>

                        </div>

                    </div>

                </form>

            </div>

        </div>
    );
}
